import { User } from "model/user";
import { TextMessageRegistrationStatus } from "type/text-message-registration-status";

export type TextingDisplayStatus = TextMessageRegistrationStatus | "PAUSED";

export type TextingStatusTone = "neutral" | "warning" | "info" | "success";

export type TextingStatusIcon = "clock" | "alert" | "check" | "paused";

export interface TextingStatusConfig {
	label: string;
	tone: TextingStatusTone;
	icon: TextingStatusIcon;
	/** Paused has no banner in Figma; the chip alone carries it. */
	banner?: {
		title: string;
		body: string;
	};
	dismissable: boolean;
}

type StatusFields = Pick<User, "textMessageOptIn" | "dripMMSOptOutDate" | "textMessageRegistrationStatus">;

/**
 * The one status the Texting page shows. Paused is the client's own switch, so it wins over the
 * registration status the server derives; a client who finished setup never sees a blank status.
 */
export const displayStatus = (user: StatusFields): TextingDisplayStatus | undefined => {
	if (!user.textMessageOptIn) {
		return undefined;
	}
	if (user.dripMMSOptOutDate) {
		return "PAUSED";
	}
	return user.textMessageRegistrationStatus || "SUBMISSION_IN_PROGRESS";
};

const configs: Record<TextingDisplayStatus, TextingStatusConfig> = {
	SUBMISSION_IN_PROGRESS: {
		label: "Submission In Progress",
		tone: "neutral",
		icon: "clock",
		banner: {
			title: "Submission In Progress",
			body: "We're working on submitting your business and campaign for approval. No action is needed from you.",
		},
		dismissable: false,
	},
	MOBILE_VERIFICATION_PENDING: {
		label: "Mobile Verification Pending",
		tone: "warning",
		icon: "alert",
		banner: {
			title: "Mobile Verification Pending",
			body: "Reply \"YES\" to the text sent to your mobile number to enable texting.",
		},
		dismissable: false,
	},
	CAMPAIGN_APPROVAL_PENDING: {
		label: "Campaign Approval Pending",
		tone: "info",
		icon: "clock",
		banner: {
			title: "Campaign Approval Pending",
			body: "Your brand and campaign are being reviewed. Texts won’t send until both are approved.",
		},
		dismissable: false,
	},
	ACTIVE: {
		label: "Active",
		tone: "success",
		icon: "check",
		banner: {
			title: "Texting Active",
			body: "You're all approved! Texts will now start sending to leads who opt in.",
		},
		dismissable: true,
	},
	PAUSED: {
		label: "Paused",
		tone: "neutral",
		icon: "paused",
		dismissable: false,
	},
};

export const statusConfig = (status: TextingDisplayStatus): TextingStatusConfig => configs[status];
